import React, { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent } from '@/components/ui/card'
import { MoreHorizontal, Pencil, RotateCcw, Trash2, Video } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import type { AdminClip } from './clip.types'
import type { ClipCategory } from '../clip-categories/clip-category.types'

// ----------------------------------------------------------------------

interface ClipsTableProps {
  clips: AdminClip[]
  loading: boolean
  onEdit?: (clip: AdminClip) => void
  onDelete?: (clip: AdminClip) => void
  onRetry?: (clip: AdminClip) => void
  onRowClick?: (clip: AdminClip) => void
  onTogglePublish?: (clip: AdminClip, isPublished: boolean) => void
  categories?: ClipCategory[]
}

const formatDuration = (sec: number) => {
  const m = Math.floor(sec / 60)
  // Floor the seconds too: durationSec can be a raw float (e.g. 249.0572…),
  // and without flooring, padStart is a no-op on the long decimal string
  // (renders "4:9.057233999999994" instead of "4:09").
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

const formatUploadDate = (iso: string) => {
  const d = new Date(iso)
  return isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
}

function StatusBadge({
  status,
  errorCode,
  errorMessage,
}: {
  status: AdminClip['status']
  errorCode?: string
  errorMessage?: string
}) {
  if (status === 'processing') {
    return (
      <Badge className="text-xs bg-yellow-100 text-yellow-800 border-yellow-200 animate-pulse">
        กำลังประมวลผล
      </Badge>
    )
  }
  if (status === 'failed') {
    return (
      <div className="space-y-0.5">
        <Badge
          className="text-xs bg-red-100 text-red-800 border-red-200"
          title={errorMessage || errorCode || undefined}
        >
          ล้มเหลว
        </Badge>
        {errorCode && (
          <p
            className="max-w-[10rem] truncate text-[10px] text-red-600/80"
            title={errorMessage || errorCode}
          >
            {errorCode}
          </p>
        )}
      </div>
    )
  }
  return (
    <Badge className="text-xs bg-green-100 text-green-800 border-green-200">
      พร้อม
    </Badge>
  )
}

export default function ClipsTable({
  clips: initialClips,
  loading,
  onEdit,
  onDelete,
  onRetry,
  onRowClick,
  onTogglePublish,
  categories = [],
}: ClipsTableProps) {
  const [clips, setClips] = useState<AdminClip[]>(initialClips)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const categoryLabelById = React.useMemo(
    () => new Map(categories.map((category) => [category.id, category.label])),
    [categories],
  )

  React.useEffect(() => {
    setClips(initialClips)
  }, [initialClips])

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">ภาพปก</TableHead>
                <TableHead>ชื่อคลิป</TableHead>
                <TableHead className="w-20">ความยาว</TableHead>
                <TableHead className="w-32">หมวดหมู่</TableHead>
                <TableHead className="w-28">สถานะ</TableHead>
                <TableHead className="w-36">วันที่อัพโหลด</TableHead>
                <TableHead className="w-24">เผยแพร่</TableHead>
                <TableHead className="w-16">การจัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="w-20 h-12 bg-gray-200 rounded-lg animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-10 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-10 bg-gray-200 rounded-full animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (clips.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <Video className="mx-auto mb-3 h-10 w-10 text-gray-300" />
        <p className="text-base text-muted-foreground">ยังไม่มีคลิปในระบบ</p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">ภาพปก</TableHead>
              <TableHead>ชื่อคลิป</TableHead>
              <TableHead className="w-20">ความยาว</TableHead>
              <TableHead className="w-32">หมวดหมู่</TableHead>
              <TableHead className="w-28">สถานะ</TableHead>
              <TableHead className="w-36">วันที่อัพโหลด</TableHead>
              <TableHead className="w-24">เผยแพร่</TableHead>
              <TableHead className="w-16">การจัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clips.map((clip) => (
              <TableRow
                key={clip.id}
                onClick={() => onRowClick?.(clip)}
                className="cursor-pointer hover:bg-muted/50"
              >
                <TableCell>
                  <div className="w-20 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {clip.thumbnailUrl ? (
                      <img
                        src={clip.thumbnailUrl}
                        alt={clip.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-black line-clamp-2 max-w-xs">
                      {clip.title}
                    </p>
                    {clip.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {clip.tags.slice(0, 3).map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-xs px-1.5 py-0"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {formatDuration(clip.durationSec)}
                    </span>
                    {clip.aspectRatio && (
                      <p className="text-xs text-muted-foreground">
                        {clip.aspectRatio}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {categoryLabelById.get(clip.category) ?? 'ทั้งหมด'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    status={clip.status}
                    errorCode={clip.transcodeErrorCode}
                    errorMessage={clip.transcodeErrorMessage}
                  />
                </TableCell>
                <TableCell>
                  <span className="text-sm tabular-nums text-muted-foreground whitespace-nowrap">
                    {formatUploadDate(clip.createdAt)}
                  </span>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={clip.isPublished}
                    disabled={clip.status !== 'ready'}
                    onCheckedChange={(checked) => {
                      setClips((prev) =>
                        prev.map((c) =>
                          c.id === clip.id ? { ...c, isPublished: checked } : c,
                        ),
                      )
                      onTogglePublish?.(clip, checked)
                    }}
                  />
                </TableCell>
                <TableCell
                  className="text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenu
                    open={openDropdownId === clip.id}
                    onOpenChange={(open) =>
                      setOpenDropdownId(open ? clip.id : null)
                    }
                  >
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(clip)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>
                      )}
                      {onRetry &&
                        clip.status === 'failed' &&
                        clip.storageProvider === 'r2' && (
                          <DropdownMenuItem onClick={() => onRetry(clip)}>
                            <RotateCcw className="mr-2 h-4 w-4" />
                            ลองใหม่
                          </DropdownMenuItem>
                        )}
                      {onDelete && (
                        <DropdownMenuItem
                          onClick={() => onDelete(clip)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          ลบ
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
